import { useState, useMemo, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/project/karuna/components/ui/card";
import { Button } from "@/project/karuna/components/ui/button";
import { Input } from "@/project/karuna/components/ui/input";
import { Label } from "@/project/karuna/components/ui/label";
import {
  Heart,
  Copy,
  CheckCircle2,
  Building2,
  Smartphone,
  X,
} from "lucide-react";
import PublicLayout from "./PublicLayout";
import qrCode from "@/project/karuna/assets/karun95514barodampayVerified Merchant_page-0001.jpg";
import { createForm } from "@formily/core";
import SchemaFormRender from "@/components/Schema/SchemaFormRender";
import axios from "axios";
import { notify } from "@/components/common/Notification";

type Step = "form" | "payment" | "thankyou";

const SCHEMA_NAME = "Karuna Donation";

const DONATE_SCHEMA = {
  form: { layout: "vertical" },
  schema: {
    type: "object",
    "x-designable-id": "donate_root",
    properties: {
      layoutContainer: {
        type: "void",
        "x-component": "FormLayout",
        "x-component-props": { maxColumns: 1, minColumns: 1, columnGap: 20 },
        name: "layoutContainer",
        "x-designable-id": "donate_layout_001",
        "x-index": 0,
        properties: {
          amountCard: {
            type: "void",
            "x-component": "Card",
            "x-component-props": { title: "Donation Amount" },
            name: "amountCard",
            "x-designable-id": "donate_amount_card",
            "x-index": 0,
            properties: {
              amount: {
                type: "string",
                title: "Select Amount",
                "x-decorator": "FormItem",
                "x-decorator-props": { layout: "vertical" },
                "x-component": "Select",
                "x-component-props": {
                  placeholder: "Select a donation amount",
                },
                name: "amount",
                "x-designable-id": "donate_amount",
                "x-index": 0,
                enum: [
                  { label: "₹500", value: "500" },
                  { label: "₹1,000", value: "1000" },
                  { label: "₹2,500", value: "2500" },
                  { label: "₹5,000", value: "5000" },
                  { label: "₹10,000", value: "10000" },
                  { label: "Other Amount", value: "other" },
                ],
                "x-validator": [
                  {
                    required: true,
                    message: "Please select a donation amount",
                  },
                ],
              },
              custom_amount: {
                type: "number",
                title: "Custom Amount (₹)",
                "x-decorator": "FormItem",
                "x-decorator-props": { layout: "vertical" },
                "x-component": "NumberPicker",
                "x-component-props": { placeholder: "e.g. 750", min: 1 },
                name: "custom_amount",
                "x-designable-id": "donate_custom_amount",
                "x-index": 1,
                "x-reactions": {
                  dependencies: ["amount"],
                  fulfill: { state: { visible: "{{$deps[0] === 'other'}}" } },
                },
                "x-validator": [
                  { required: true, message: "Please enter a custom amount" },
                ],
              },
            },
          },
          donorCard: {
            type: "void",
            "x-component": "Card",
            "x-component-props": { title: "Donor Details" },
            name: "donorCard",
            "x-designable-id": "donate_donor_card",
            "x-index": 1,
            properties: {
              donorGrid: {
                type: "void",
                "x-component": "FormGrid",
                "x-component-props": {
                  maxColumns: 2,
                  minColumns: 2,
                  columnGap: 20,
                  "is-in-grid": true,
                },
                name: "donorGrid",
                "x-designable-id": "donate_donor_grid",
                "x-index": 0,
                properties: {
                  name: {
                    type: "string",
                    title: "Name on Receipt",
                    "x-decorator": "FormItem",
                    "x-decorator-props": { layout: "vertical" },
                    "x-component": "Input",
                    "x-component-props": {
                      gridSpan: 1,
                      placeholder: "Full name as on PAN/ID",
                      "is-in-grid": true,
                    },
                    name: "name",
                    "x-designable-id": "donate_name",
                    "x-index": 0,
                    "x-validator": [
                      { required: true, message: "Name is required" },
                    ],
                  },
                  mobile: {
                    type: "string",
                    title: "Mobile Number",
                    "x-decorator": "FormItem",
                    "x-decorator-props": { layout: "vertical" },
                    "x-component": "Input",
                    "x-component-props": {
                      gridSpan: 1,
                      placeholder: "+91 98765 43210",
                      "is-in-grid": true,
                      onKeyDown: (e: any) => {
                        if (
                          !/[0-9]/.test(e.key) &&
                          ![
                            "Backspace",
                            "Delete",
                            "Tab",
                            "ArrowLeft",
                            "ArrowRight",
                          ].includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                      },
                    },
                    name: "mobile",
                    "x-designable-id": "donate_mobile",
                    "x-index": 1,
                    "x-validator": [
                      { required: true, message: "Mobile number is required" },
                      {
                        pattern: "^[0-9]{10}$",
                        message: "Mobile must be exactly 10 digits",
                      },
                    ],
                  },
                },
              },
              require_80g: {
                type: "string",
                title: "80G Receipt Required?",
                "x-decorator": "FormItem",
                "x-decorator-props": { layout: "vertical" },
                "x-component": "Radio.Group",
                name: "require_80g",
                "x-designable-id": "donate_80g",
                "x-index": 1,
                default: "no",
                enum: [
                  { label: "Yes", value: "yes" },
                  { label: "No", value: "no" },
                ],
                "x-validator": [
                  {
                    required: true,
                    message: "Please select whether 80G receipt is required",
                  },
                ],
              },
              pan: {
                type: "string",
                title: "PAN Number",
                "x-decorator": "FormItem",
                "x-decorator-props": { layout: "vertical" },
                "x-component": "Input",
                "x-component-props": {
                  placeholder: "ABCDE1234F",
                  maxLength: 10,
                },
                name: "pan",
                "x-designable-id": "donate_pan",
                "x-index": 2,
                "x-reactions": {
                  dependencies: ["require_80g"],
                  fulfill: { state: { visible: "{{$deps[0] === 'yes'}}" } },
                },
                "x-validator": [
                  {
                    required: true,
                    message: "PAN is required for 80G receipt",
                  },
                ],
              },
            },
          },
          receiptCard: {
            type: "void",
            "x-component": "Card",
            "x-component-props": { title: "Receipt Preference" },
            name: "receiptCard",
            "x-designable-id": "donate_receipt_card",
            "x-index": 2,
            properties: {
              receipt_mode: {
                type: "string",
                title: "How would you like to receive the receipt?",
                "x-decorator": "FormItem",
                "x-decorator-props": { layout: "vertical" },
                "x-component": "Radio.Group",
                name: "receipt_mode",
                "x-designable-id": "donate_receipt_mode",
                "x-index": 0,
                default: "email",
                enum: [
                  { label: "Email", value: "email" },
                  { label: "WhatsApp", value: "whatsapp" },
                ],
                "x-validator": [
                  { required: true, message: "Please select receipt mode" },
                ],
              },
              email: {
                type: "string",
                title: "Email ID",
                "x-decorator": "FormItem",
                "x-decorator-props": { layout: "vertical" },
                "x-component": "Input",
                "x-component-props": { placeholder: "you@example.com" },
                name: "email",
                "x-designable-id": "donate_email",
                "x-index": 1,
                "x-reactions": {
                  dependencies: ["receipt_mode"],
                  fulfill: { state: { visible: "{{$deps[0] === 'email'}}" } },
                },
                "x-validator": [
                  { required: true, message: "Email is required" },
                  "email",
                ],
              },
              whatsapp: {
                type: "string",
                title: "WhatsApp Number",
                "x-decorator": "FormItem",
                "x-decorator-props": { layout: "vertical" },
                "x-component": "Input",
                "x-component-props": { placeholder: "+91 98765 43210" },
                name: "whatsapp",
                "x-designable-id": "donate_whatsapp",
                "x-index": 2,
                "x-reactions": {
                  dependencies: ["receipt_mode"],
                  fulfill: {
                    state: { visible: "{{$deps[0] === 'whatsapp'}}" },
                  },
                },
                "x-validator": [
                  { required: true, message: "WhatsApp number is required" },
                  {
                    pattern: "^[0-9]{10}$",
                    message: "Must be exactly 10 digits",
                  },
                ],
              },
            },
          },
          ackCard: {
            type: "void",
            "x-component": "Card",
            "x-component-props": { title: "Public Acknowledgement" },
            name: "ackCard",
            "x-designable-id": "donate_ack_card",
            "x-index": 3,
            properties: {
              publish_name: {
                type: "string",
                title: "May we publish your name as a donor?",
                "x-decorator": "FormItem",
                "x-decorator-props": { layout: "vertical" },
                "x-component": "Radio.Group",
                name: "publish_name",
                "x-designable-id": "donate_publish_name",
                "x-index": 0,
                default: "yes",
                enum: [
                  { label: "Yes", value: "yes" },
                  { label: "No", value: "no" },
                ],
                "x-validator": [
                  { required: true, message: "Please select an option" },
                ],
              },
            },
          },
          declarationCard: {
            type: "void",
            "x-component": "Card",
            "x-component-props": { title: "Declaration" },
            name: "declarationCard",
            "x-designable-id": "donate_declaration_card",
            "x-index": 4,
            properties: {
              declaration: {
                type: "boolean",
                title:
                  "I confirm that the details provided above are correct and may be used by Karuna International for issuing the donation receipt and related communication.",
                "x-decorator": "FormItem",
                "x-decorator-props": { layout: "vertical" },
                "x-component": "Switch",
                name: "declaration",
                "x-designable-id": "donate_declaration",
                "x-index": 0,
                "x-validator": [
                  { required: true, message: "Please accept the declaration" },
                ],
              },
              payment_reference: {
                type: "string",
                title: "Payment Reference / Transaction ID",
                "x-decorator": "FormItem",
                "x-decorator-props": { layout: "vertical" },
                "x-component": "Input",
                "x-component-props": {
                  placeholder:
                    "Enter UPI transaction ID or bank reference number",
                },
                name: "payment_reference",
                "x-designable-id": "donate_payment_ref",
                "x-index": 1,
                "x-display": "none",
                "x-validator": [],
              },
            },
          },
        },
      },
    },
  },
};

const Donate = () => {
  const [step, setStep] = useState<Step>("form");
  const [copied, setCopied] = useState<string | null>(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paymentRef, setPaymentRef] = useState("");
  const [paymentRefError, setPaymentRefError] = useState("");

  const form = useMemo(() => createForm({}), []);
  const [declarationAccepted, setDeclarationAccepted] = useState(false);

  useEffect(() => {
    const id = form.subscribe(() => {
      setDeclarationAccepted(!!form.values?.declaration);
    });
    return () => form.unsubscribe(id);
  }, [form]);

  const displayAmount = (() => {
    const val = form.values?.amount;
    if (val === "other") {
      return String(form.values?.custom_amount ?? "");
    }
    return val ? Number(val).toLocaleString("en-IN") : "";
  })();

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleProceedToPayment = async () => {
    try {
      await form.validate();
      setStep("payment");
    } catch {
      // validation errors are shown inline by Formily
    }
  };

  const handleCompletePayment = async () => {
    if (!paymentRef.trim()) {
      setPaymentRefError("Payment Reference / Transaction ID is required");
      return;
    }
    setPaymentRefError("");
    setSubmitting(true);
    try {
      await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/formily/dynamic-model/${SCHEMA_NAME}/`,
        { ...form.values, payment_reference: paymentRef },
        {
          headers: {
            "Content-Type": "application/json",
            "X-Tenant-Id": import.meta.env.VITE_X_TENANTID,
          },
        }
      );
      setStep("thankyou");
    } catch {
      notify.error(
        "Submission Failed",
        "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (step === "thankyou") {
    return (
      <PublicLayout>
        <section className="flex min-h-[70vh] items-center justify-center bg-muted/30 py-20">
          <div className="mx-auto max-w-2xl px-4 text-center">
            <CheckCircle2 className="mx-auto mb-6 h-20 w-20 text-primary" />
            <h1 className="mb-4 text-4xl font-bold">Thank You!</h1>
            <p className="mb-8 text-lg text-muted-foreground">
              Thank you for supporting Karuna International. Your contribution
              helps us take activity-based humane education to more students and
              schools. The receipt will be issued after payment confirmation.
            </p>
            <Button
              onClick={() => {
                setStep("form");
                setPaymentRef("");
                setPaymentRefError("");
                form.reset();
              }}
            >
              Make Another Donation
            </Button>
          </div>
        </section>
      </PublicLayout>
    );
  }

  if (step === "payment") {
    return (
      <PublicLayout>
        <section className="w-full bg-gradient-hero py-20 text-white">
          <div className="container mx-auto px-4 text-center">
            <h1 className="mb-4 text-5xl font-bold">Complete Your Donation</h1>
            <p className="text-xl">Amount: ₹{displayAmount}</p>
          </div>
        </section>

        <section className="bg-muted/30 py-16">
          <div className="container mx-auto max-w-3xl px-4">
            <p className="mb-8 text-center text-muted-foreground">
              Please complete the payment using any of the options below. After
              payment, share the reference number or screenshot with us so we
              can issue your receipt.
            </p>

            <div className="grid gap-6 md:grid-cols-2">
              {/* UPI Option */}
              <Card className="shadow-strong">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <Smartphone className="h-8 w-8 text-primary" />
                    <CardTitle>Pay by UPI</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div
                    className="flex cursor-zoom-in items-center justify-center overflow-hidden rounded-lg"
                    onClick={() => setQrOpen(true)}
                    title="Click to enlarge"
                  >
                    <img
                      src={qrCode}
                      alt="UPI QR Code"
                      className="w-full max-w-xs object-contain"
                    />
                  </div>
                  <p className="text-center text-xs text-muted-foreground">
                    Tap to enlarge & scan
                  </p>
                  <div className="rounded-md bg-muted p-3">
                    <p className="mb-1 text-xs text-muted-foreground">UPI ID</p>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm font-medium">
                        karunainternational@barodampay
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2"
                        onClick={() =>
                          handleCopy("karunainternational@barodampay", "upi")
                        }
                      >
                        {copied === "upi" ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Account Name: Karuna International
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Bank Transfer Option */}
              <Card className="shadow-strong">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <Building2 className="h-8 w-8 text-secondary" />
                    <CardTitle>Bank Transfer</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    { label: "Account Name", value: "Karuna International" },
                    { label: "Bank", value: "Bank of Baroda" },
                    {
                      label: "Account No.",
                      value: "72090100010793",
                      copyKey: "accno",
                    },
                    {
                      label: "Branch",
                      value: "Dr. Alagappa Road, Purasawakkam",
                    },
                    { label: "IFSC", value: "BARB0DBPURA", copyKey: "ifsc" },
                    {
                      label: "UPI ID",
                      value: "karunainternational@barodampay",
                      copyKey: "bankupi",
                    },
                  ].map(({ label, value, copyKey }) => (
                    <div key={label} className="rounded-md bg-muted px-3 py-2">
                      <p className="text-xs text-muted-foreground">{label}</p>
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-sm font-medium">
                          {value}
                        </span>
                        {copyKey && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2"
                            onClick={() => handleCopy(value, copyKey)}
                          >
                            {copied === copyKey ? (
                              <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                            ) : (
                              <Copy className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground">
                    After transfer, please share the payment reference number or
                    screenshot so that we can match your donation and issue the
                    receipt.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Payment reference */}
            <Card className="mt-6 shadow-strong">
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <Label htmlFor="payref">
                    Payment Reference Number / Transaction ID{" "}
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="payref"
                    value={paymentRef}
                    onChange={e => {
                      setPaymentRef(e.target.value);
                      if (e.target.value.trim()) {
                        setPaymentRefError("");
                      }
                    }}
                    onKeyDown={e => {
                      if (
                        !/[0-9]/.test(e.key) &&
                        ![
                          "Backspace",
                          "Delete",
                          "Tab",
                          "ArrowLeft",
                          "ArrowRight",
                        ].includes(e.key)
                      ) {
                        e.preventDefault();
                      }
                    }}
                    placeholder="Enter UPI transaction ID or bank reference number"
                    className={
                      paymentRefError
                        ? "border-red-500 focus-visible:ring-red-500"
                        : ""
                    }
                  />
                  {paymentRefError && (
                    <p className="text-xs text-red-500">{paymentRefError}</p>
                  )}
                </div>
                <Button
                  className="mt-4 w-full"
                  onClick={handleCompletePayment}
                  disabled={submitting}
                >
                  {submitting ? "Saving..." : "I Have Completed the Payment"}
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>

        {qrOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            onClick={() => setQrOpen(false)}
          >
            <div
              className="relative w-full max-w-sm"
              onClick={e => e.stopPropagation()}
            >
              <button
                className="absolute -right-3 -top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-lg"
                onClick={() => setQrOpen(false)}
              >
                <X className="h-4 w-4" />
              </button>
              <img
                src={qrCode}
                alt="UPI QR Code"
                className="w-full rounded-xl shadow-2xl"
              />
              <p className="mt-3 text-center text-sm text-white">
                Scan to pay via any UPI app
              </p>
            </div>
          </div>
        )}
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="w-full bg-gradient-hero py-20 text-white">
        <div className="container mx-auto px-4 text-center">
          <Heart className="mx-auto mb-6 h-16 w-16" />
          <h1 className="mb-6 text-5xl font-bold">Donate</h1>
          <p className="mx-auto max-w-3xl text-xl">
            Support humane education. Help us build a more compassionate
            generation.
          </p>
        </div>
      </section>

      {/* Why Donate */}
      <section className="bg-background py-16">
        <div className="container mx-auto max-w-4xl px-4">
          <h2 className="mb-8 text-center text-4xl font-bold">Why Donate?</h2>
          <div className="mx-auto mb-6 h-1 w-24 bg-primary" />
          <div className="space-y-5 text-lg leading-relaxed text-muted-foreground">
            <p>
              Karuna International has been working since 1995 to sow the seeds
              of compassion, kindness, animal welfare, humane values and ecology
              in young minds through Karuna Clubs in schools.
            </p>
            <p>
              Your donation helps us take this work to more students and
              teachers through activity-based education, training programmes,
              study material, competitions, awareness campaigns and regular
              school-level initiatives.
            </p>
            <p>
              In a world where academic success is often given more attention
              than humane values, Karuna International works to gently remind
              children that kindness matters.
            </p>
            <p>
              Every contribution helps us strengthen this movement and reach
              more schools across India.
            </p>
            <p className="font-semibold text-foreground">
              Donate to support humane education. Help us build a more
              compassionate generation.
            </p>
          </div>
        </div>
      </section>

      {/* Donation Form */}
      <section className="bg-muted/30 py-16">
        <div className="container mx-auto max-w-3xl px-4">
          <div className="mb-10 text-center">
            <h2 className="mb-4 text-4xl font-bold">Make a Donation</h2>
            <div className="mx-auto mb-4 h-1 w-24 bg-primary" />
            <p className="text-lg text-muted-foreground">
              Fill in the details below to proceed with your contribution
            </p>
          </div>
          <SchemaFormRender form={form} schemaJson={DONATE_SCHEMA} />
          <Button
            size="lg"
            className="mt-6 w-full text-base"
            onClick={handleProceedToPayment}
            disabled={!declarationAccepted}
          >
            Proceed to Payment
          </Button>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Donate;
